/**
 * Every tunable threshold of the guided selfie lives here, so tuning on real
 * phones means editing one file. Values are initial estimates (not yet
 * calibrated on a range of devices). Landmark coordinates are MediaPipe
 * normalized image coordinates (0..1, origin top-left).
 */

export const SELFIE_CONFIG = {
  /** Camera constraints requested from getUserMedia (front camera). */
  camera: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 960 } },

  /** Self-hosted assets (apps/admin/public/mediapipe). No third-party CDN. */
  assets: {
    wasmDir: '/mediapipe/wasm',
    model: '/mediapipe/face_landmarker.task',
  },
  /** Give up on MediaPipe (old device, WASM blocked, slow network) after this. */
  loadTimeoutMs: 20_000,
  /** Detection throttle: ~12 fps is plenty and keeps phones cool. */
  detectIntervalMs: 1000 / 12,

  /** The face must be at least this wide, as a fraction of frame width. */
  minFaceWidth: 0.3,
  /** ...and not so big that it is cropped / too close. */
  maxFaceWidth: 0.85,
  /** Face-box centre must be within this distance of the frame centre. */
  maxCentreOffsetX: 0.15,
  maxCentreOffsetY: 0.18,
  /** Face box must keep this margin from every frame edge (fully visible). */
  edgeMargin: 0.01,

  /** Mean luminance (0..255) of the face box must be inside this band. */
  minLuma: 70,
  maxLuma: 205,
  /**
   * Variance of the Laplacian of the grayscale face box, measured after
   * downscaling to `statsWidth` px wide (so it does not depend on the
   * camera resolution). Below this the picture is blurry.
   */
  minSharpness: 35,
  statsWidth: 96,

  /** Auto-capture once this many of the 4 checks (one face, close enough,
   * centred, light & sharp) pass... */
  minPassedChecks: 3,
  /** ...for this long (a progress bar counts it down). */
  stableMs: 7000,
  /** A brief dip below the bar (a blink, a shake) shorter than this does not
   * restart the countdown. */
  okGraceMs: 700,

  /**
   * Anti-lockout: thresholds are uncalibrated and the flow is camera-only, so
   * after this long on the step a secondary "take the photo anyway" button
   * appears (needs only one face in view).
   */
  troubleAfterMs: 45_000,
  /** Consecutive detectForVideo errors before switching to manual capture. */
  maxDetectErrors: 30,

  /** Captured image. */
  capture: {
    /** Face width as a share of the crop width (target; allowed 40-60%). */
    faceShare: 0.5,
    /** Portrait crop aspect (width / height). */
    aspect: 3 / 4,
    /** Minimum short side in px; we never upscale. */
    minShortSide: 480,
    jpegQuality: 0.9,
    /** Server limit is 2 MB; keep a safety margin. */
    maxBytes: 1.9 * 1024 * 1024,
  },
} as const
